import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ActionButton } from '@/components/ui/action-button';
import { supabase } from '@/lib/supabase';
import type { SchoolLevel } from '@/types';

export default function SchoolLevels() {
  const [schoolLevels, setSchoolLevels] = useState<SchoolLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSchoolLevel, setSelectedSchoolLevel] = useState<SchoolLevel | null>(null);
  const [newSchoolLevelName, setNewSchoolLevelName] = useState('');

  useEffect(() => {
    fetchSchoolLevels();
  }, []);

  const fetchSchoolLevels = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_school_levels')
        .select('*')
        .order('name');

      if (error) throw error;
      setSchoolLevels(data);
    } catch (error) {
      console.error('Error fetching school levels:', error);
      toast.error('Failed to fetch school levels');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (selectedSchoolLevel) {
        // Update existing school level
        const { error } = await supabase
          .from('sms_school_levels')
          .update({ name: newSchoolLevelName })
          .eq('id', selectedSchoolLevel.id);

        if (error) throw error;
        toast.success('School level updated successfully');
      } else {
        // Create new school level
        const { error } = await supabase
          .from('sms_school_levels')
          .insert({ name: newSchoolLevelName });

        if (error) throw error;
        toast.success('School level created successfully');
      }

      setModalOpen(false);
      fetchSchoolLevels();
    } catch (error) {
      console.error('Error saving school level:', error);
      toast.error('Failed to save school level');
    }
  };

  const handleDelete = async () => {
    if (!selectedSchoolLevel) return;

    try {
      const { error } = await supabase
        .from('sms_school_levels')
        .delete()
        .eq('id', selectedSchoolLevel.id);

      if (error) throw error;

      toast.success('School level deleted successfully');
      setDeleteDialogOpen(false);
      fetchSchoolLevels();
    } catch (error) {
      console.error('Error deleting school level:', error);
      toast.error('Failed to delete school level');
    }
  };

  const columns = [
    { header: 'Name', accessorKey: 'name' },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <ActionButton
            icon={Pencil}
            variant="edit"
            onClick={() => {
              setSelectedSchoolLevel(row.original);
              setNewSchoolLevelName(row.original.name);
              setModalOpen(true);
            }}
          />
          <ActionButton
            icon={Trash2}
            variant="delete"
            onClick={() => {
              setSelectedSchoolLevel(row.original);
              setDeleteDialogOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">School Levels</h1>
        <Button
          onClick={() => {
            setSelectedSchoolLevel(null);
            setNewSchoolLevelName('');
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add School Level
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={schoolLevels}
        isLoading={isLoading}
      />

      <Modal
        title={selectedSchoolLevel ? 'Edit School Level' : 'Add School Level'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSubmit}
      >
        <div>
          <label className="text-sm font-medium mb-1 block">Name</label>
          <Input
            value={newSchoolLevelName}
            onChange={(e) => setNewSchoolLevelName(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        title="Delete School Level"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="destructive"
      >
        <p>Are you sure you want to delete this school level? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}